import {Injectable} from '@angular/core';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import {FormGroup} from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import {lastValueFrom} from 'rxjs';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

@Injectable({
    providedIn: 'root'
})
export class AgreementPdfService {

    constructor(private http: HttpClient) {
    }

    public async imageToBase64(src: string): Promise<string> {
        let blob = await lastValueFrom(this.http.get(src, {responseType: 'blob'}));
        return new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                if (typeof reader.result === 'string') {
                    resolve(reader.result);
                } else {
                    reject(new Error('Failed to convert image to base64 string'));
                }
            };
            reader.onerror = () => reject(new Error('Error reading image file'));
            reader.readAsDataURL(blob);
        });
    }

    generateAgreementPdf(agreementForm: FormGroup, leftSealBase64: string, rightSealBase64: string, downloadFile?: boolean): Promise<Blob> {
        const formValues = agreementForm.value;
        const name = formValues.name || 'N/A';
        const address = formValues.address || 'N/A';
        const accountNumber = formValues.accountNumber || 'N/A';
        const phone = formValues.phone || 'N/A';
        const date = formValues.date
            ? new Date(formValues.date).toLocaleDateString()
            : new Date().toLocaleDateString();

        const documentDefinition = {
            info: {
                title: `Water Utility Assistance Repayment Agreement`,
                author: 'United Way of Greater St. Louis',
                subject: `Repayment Agreement for ${name}`,
            },
            content: [
                {
                    columns: [
                        {
                            image: leftSealBase64,
                            width: 50,
                            alignment: 'left',
                            margin: [0, 0, 8, 0]
                        },
                        {
                            stack: [
                                {
                                    text: 'OFFICE OF THE COLLECTOR OF REVENUE',
                                    style: 'headerTitle',
                                    alignment: 'center'
                                },
                                {
                                    text: 'GREGORY F.X. DALY',
                                    style: 'headerSubTitle',
                                    alignment: 'center'
                                },
                                {
                                    text: 'City of St. Louis — Water-Refuse Collections',
                                    style: 'headerAddress',
                                    alignment: 'center'
                                },
                                {
                                    text: '1200 Market St. Room 110, St. Louis, MO 63103',
                                    style: 'headerAddress',
                                    alignment: 'center'
                                },
                                {
                                    text: '(314) 622-3628 • Fax (314) 613-7041 • waterrefusecol@stlouis-mo.gov',
                                    style: 'headerContact',
                                    alignment: 'center'
                                }
                            ],
                            width: '*',
                            margin: [15, 3, 0, 3]
                        },
                        {
                            image: rightSealBase64,
                            width: 75,
                            alignment: 'right',
                            margin: [8, 0, 0, 0]
                        }
                    ],
                    margin: [15, 15, 15, 15]
                },
                {
                    text: 'Water Utility Assistance Repayment Agreement',
                    style: 'agreementTitle',
                    alignment: 'center',
                    margin: [0, 0, 0, 10]
                },
                {
                    text: [
                        {text: 'I, '},
                        {text: ` ${name}`, bold: true},
                        {text: ', agree to initiate a repayment agreement for my water and refuse bill that corresponds to the service address of: '},
                        {text: `${address}`, bold: true},
                        {text: '.'}
                    ],
                    margin: [0, 0, 0, 8]
                },
                {
                    text: [
                        'If the balance due after receiving Water Utility Assistance is less than $1,000, ' +
                        'the remaining balance will be split into twelve equal payments to be paid monthly over twelve months. ' +
                        'If the remaining balance is over $1,000, it will be split into twenty-four equal payments, to be paid monthly over twenty-four months. ' +
                        'In addition to the past due balance owed on the account, this payment agreement will include projected future water and refuse service charges for the duration of the repayment period. ' +
                        'The total repayment amount may be higher than the current balance because of the inclusion of these projected future charges. Once assistance is approved and applied, the Collector of Revenue’s office will mail a copy of the repayment agreement to the address on file.'
                    ],
                    margin: [0, 0, 0, 8]
                },
                {
                    text: 'Failure to make a payment may result in the repayment agreement being voided and possible disconnection of service.',
                    margin: [0, 0, 0, 8]
                },
                {
                    text: 'Please check each statement below signifying your agreement.',
                    bold: true,
                    margin: [0, 0, 0, 8]
                },
                ...[1, 2, 3, 4, 5, 6].map(i => ({
                    columns: [
                        {
                            image: formValues[`section${i}`] ? 'checked' : 'unchecked',
                            height: 10,
                            width: 10,
                            margin: [0, 2, 0, 0]
                        },
                        {
                            text: (() => {
                                switch (i) {
                                    case 1:
                                        return 'I acknowledge that I am entering into a repayment agreement with the Collector of Revenue’s office for the total water and refuse balance due at the above-mentioned property.';
                                    case 2:
                                        return 'I acknowledge that if my bill is more than $1,000 after receiving the Water Utility Assistance, my remaining balance will be split into 24 equal payments to be paid each month for 24 months.';
                                    case 3:
                                        return 'I acknowledge that if my bill is less than $1,000 after receiving the Water Utility Assistance, my remaining balance will be split into 12 equal payments to be paid each month for 12 months.';
                                    case 4:
                                        return 'I acknowledge that this payment agreement will include my past due balance as well as projected future water and refuse service charges for the duration of the repayment period, and  that the total repayment amount may be higher than my current balance because of the inclusion of  these projected future charges.';
                                    case 5:
                                        return 'I acknowledge that the Collector of Revenue’s office will mail me a copy of my repayment agreement that includes the monthly payment amounts and payment schedule.';
                                    case 6:
                                        return 'I acknowledge that if I fail to make a payment, my repayment agreement will be void and I will be subject to possible disconnection.';
                                }
                            })(),
                            margin: [5, 0, 0, 0],
                            ...(i === 7 ? {style: 'italic'} : {})
                        }
                    ],
                    margin: [0, 4, 0, 4]
                })),
                {
                    text: 'My signature below signifies that I have knowingly entered into this repayment agreement and agree to pay on or before the appropriate dates.',
                    margin: [0, 0, 0, 8]
                },
                {
                    columns: [
                        {
                            width: '74%',
                            stack: [
                                {
                                    table: {
                                        widths: ['*'],
                                        body: [[
                                            { text: name, style: 'fieldValue' }
                                        ]]
                                    },
                                    layout: {
                                        fillColor: '#e0e0e0',
                                        hLineWidth: () => 0,
                                        vLineWidth: () => 0
                                    }
                                },
                                { text: 'Electronically Signed By', style: 'fieldLabel', margin: [0, 2, 0, 10] }
                            ]
                        },
                        {
                            width: '24%',
                            margin: [5, 0, 0, 0],
                            stack: [
                                {
                                    table: {
                                        widths: ['*'],
                                        body: [[
                                            { text: date, style: 'fieldValue' }
                                        ]]
                                    },
                                    layout: {
                                        fillColor: '#e0e0e0',
                                        hLineWidth: () => 0,
                                        vLineWidth: () => 0
                                    }
                                },
                                { text: 'Signed Date', style: 'fieldLabel', margin: [0, 2, 0, 10] }
                            ]
                        }
                    ],
                    margin: [0, 0, 0, 10]
                },
                {
                    columns: [
                        {
                            width: '49%',
                            stack: [
                                {
                                    table: {
                                        widths: ['*'],
                                        body: [[
                                            { text: accountNumber, style: 'fieldValue' }
                                        ]]
                                    },
                                    layout: {
                                        fillColor: '#e0e0e0',
                                        hLineWidth: () => 0,
                                        vLineWidth: () => 0
                                    }
                                },
                                { text: 'Account Number', style: 'fieldLabel', margin: [0, 2, 0, 10] }
                            ]
                        },
                        {
                            width: '49%',
                            margin: [5, 0, 0, 0],
                            stack: [
                                {
                                    table: {
                                        widths: ['*'],
                                        body: [[
                                            { text: phone, style: 'fieldValue' }
                                        ]]
                                    },
                                    layout: {
                                        fillColor: '#e0e0e0',
                                        hLineWidth: () => 0,
                                        vLineWidth: () => 0
                                    }
                                },
                                { text: 'Phone', style: 'fieldLabel', margin: [0, 2, 0, 10] }
                            ]
                        }
                    ],
                    margin: [0, 0, 0, 10]
                },
            ],
            styles: {
                fieldValue: {
                    fontSize: 12,
                    bold: true,
                    margin: [5, 5, 5, 5] // Padding inside the gray box
                },
                fieldLabel: {
                    fontSize: 10,
                    color: '#333333',
                    alignment: 'center',
                    fontStyle: 'italic',
                    italics: true
                },
                headerTitle: {
                    fontSize: 13,
                    bold: true
                },
                headerSubTitle: {
                    fontSize: 13,
                    bold: true
                },
                headerAddress: {
                    fontSize: 10
                },
                headerContact: {
                    fontSize: 10
                },
                agreementTitle: {
                    fontSize: 14,
                    bold: true
                },
                checkbox: {
                    fontSize: 11
                },
                italic: {
                    italics: true
                }
            },
            defaultStyle: {
                fontSize: 10,
                lineHeight: 1.15
            },
            images: {
                checked:
                    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAB4AAAAeCAQAAACROWYpAAAACXBIWXMAAA7EAAAOxAGVKw4bAAAF+2lUWHRYTUw6Y29tLmFkb2JlLnhtcAAAAAAAPD94cGFja2V0IGJlZ2luPSLvu78iIGlkPSJXNU0wTXBDZWhpSHpyZVN6TlRjemtjOWQiPz4gPHg6eG1wbWV0YSB4bWxuczp4PSJhZG9iZTpuczptZXRhLyIgeDp4bXB0az0iQWRvYmUgWE1QIENvcmUgNS42LWMxNDAgNzkuMTYwNDUxLCAyMDE3LzA1LzA2LTAxOjA4OjIxICAgICAgICAiPiA8cmRmOlJERiB4bWxuczpyZGY9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkvMDIvMjItcmRmLXN5bnRheC1ucyMiPiA8cmRmOkRlc2NyaXB0aW9uIHJkZjphYm91dD0iIiB4bWxuczp4bXA9Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC8iIHhtbG5zOmRjPSJodHRwOi8vcHVybC5vcmcvZGMvZWxlbWVudHMvMS4xLyIgeG1sbnM6cGhvdG9zaG9wPSJodHRwOi8vbnMuYWRvYmUuY29tL3Bob3Rvc2hvcC8xLjAvIiB4bWxuczp4bXBNTT0iaHR0cDovL25zLmFkb2JlLmNvbS94YXAvMS4wL21tLyIgeG1sbnM6c3RFdnQ9Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC9zVHlwZS9SZXNvdXJjZUV2ZW50IyIgeG1wOkNyZWF0b3JUb29sPSJBZG9iZSBQaG90b3Nob3AgQ0MgMjAxOCAoTWFjaW50b3NoKSIgeG1wOkNyZWF0ZURhdGU9IjIwMTktMTItMzBUMDE6Mzc6MjArMDE6MDAiIHhtcDpNb2RpZnlEYXRlPSIyMDE5LTEyLTMwVDAxOjM4OjI4KzAxOjAwIiB4bXA6TWV0YWRhdGFEYXRlPSIyMDE5LTEyLTMwVDAxOjM4OjI4KzAxOjAwIiBkYzpmb3JtYXQ9ImltYWdlL3BuZyIgcGhvdG9zaG9wOkNvbG9yTW9kZT0iMSIgcGhvdG9zaG9wOklDQ1Byb2ZpbGU9IkRvdCBHYWluIDIwJSIgeG1wTU06SW5zdGFuY2VJRD0ieG1wLmlpZDowNzVjYjZmMy1jNGIxLTRiZjctYWMyOS03YzUxMWY5MWJjYzQiIHhtcE1NOkRvY3VtZW50SUQ9ImFkb2JlOmRvY2lkOnBob3Rvc2hvcDo5ZTM1YTc3ZC0zNDM0LTI5NGQtYmEwOC1iY2I5MjYyMjBiOGIiIHhtcE1NOk9yaWdpbmFsRG9jdW1lbnRJRD0ieG1wLmRpZDowYzc2MDY3Ny0xNDcwLTRlZDUtOGU4ZS1kNTdjODJlZDk1Y2UiPiA8eG1wTU06SGlzdG9yeT4gPHJkZjpTZXE+IDxyZGY6bGkgc3RFdnQ6YWN0aW9uPSJjcmVhdGVkIiBzdEV2dDppbnN0YW5jZUlEPSJ4bXAuaWlkOjBjNzYwNjc3LTE0NzAtNGVkNS04ZThlLWQ1N2M4MmVkOTVjZSIgc3RFdnQ6d2hlbj0iMjAxOS0xMi0zMFQwMTozNzoyMCswMTowMCIgc3RFdnQ6c29mdHdhcmVBZ2VudD0iQWRvYmUgUGhvdG9zaG9wIENDIDIwMTggKE1hY2ludG9zaCkiLz4gPHJkZjpsaSBzdEV2dDphY3Rpb249InNhdmVkIiBzdEV2dDppbnN0YW5jZUlEPSJ4bXAuaWlkOjA3NWNiNmYzLWM0YjEtNGJmNy1hYzI5LTdjNTExZjkxYmNjNCIgc3RFdnQ6d2hlbj0iMjAxOS0xMi0zMFQwMTozODoyOCswMTowMCIgc3RFdnQ6c29mdHdhcmVBZ2VudD0iQWRvYmUgUGhvdG9zaG9wIENDIDIwMTggKE1hY2ludG9zaCkiIHN0RXZ0OmNoYW5nZWQ9Ii8iLz4gPC9yZGY6U2VxPiA8L3htcE1NOkhpc3Rvcnk+IDwvcmRmOkRlc2NyaXB0aW9uPiA8L3JkZjpSREY+IDwveDp4bXBtZXRhPiA8P3hwYWNrZXQgZW5kPSJyIj8+jHsR7AAAAUNJREFUOMvN1T9Lw0AYx/EviLVFxFH8M3USgyAFoUsQ0UV8F6Ui4qCTbuJg34HgptBdUATrUoxiqYMgiOBoIcW9BVED+jgkntGm9i6CmN+Sg/vAcc89dwBd5Clzj6uZGg7LJAC62UFipEgKcmroaeZj/gpcIAhl5rE1M0cJQbiCOsIrs5h8WZ4R6j72yBrhcRo+dhE8bCOcoYng/hFOMxAXb/DAHTNxcCGo7JE5LqhjsW2KP6nDcGecCv1vRdC2eJQDLllooach2hbvIghvLJJgM0QHdeq8F0x/5ETRM4b0DonF7be+Pf+y4A4bZnETok4E/XG3xxR3WhasUWeLCg2OGYnXGP1MkPwnLRmJf3UN+RfgtBGe5MnHVQShxBQZzdgcIgjXsKSu/KZmXgKxBkmKsZ6bffoAelilQs3goauyTi+8A8mhgeQlxdNWAAAAAElFTkSuQmCC',
                unchecked:
                    'data:image/png;base64,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',

            }
        };

        const pdfDoc = pdfMake.createPdf(documentDefinition);
        if (downloadFile) {
            pdfDoc.download('repayment-agreement.pdf');
        }
        return new Promise<Blob>((resolve, reject) => {
            pdfDoc.getBlob((blob) => {
                if (blob) {
                    resolve(blob);
                } else {
                    reject(new Error('Failed to generate PDF Blob'));
                }
            }, (error) => {
                reject(new Error('Error generating PDF Blob: ' + error));
            });
        });
    }

}
